import { useQueries } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarDays, Users } from 'lucide-react'
import { EmptyState } from '@/components/app/empty-state'
import { Screen } from '@/components/app/screen'
import { ScreenHeader } from '@/components/app/screen-header'
import { Skeleton } from '@/components/ui/skeleton'
import { apiGetPaginated } from '@/lib/api'
import { ROLE_LABELS, SERVICE_TYPE_LABELS } from '@/lib/constants'
import { formatDateFR, formatFCFA } from '@/lib/format'
import { currentMonth, currentYear, monthBounds } from '@/lib/period'
import { type Bilan, bilanQuery } from '@/lib/queries/finance'
import { recentServicesQuery, type Service } from '@/lib/queries/services'
import { canManageMembers, canViewFinance } from '@/lib/roles'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_staff/accueil/')({
  component: DashboardScreen,
})

function DashboardScreen() {
  const { roles } = Route.useRouteContext()

  // Calculés au rendu et non au chargement du module : une session ouverte
  // pendant un changement de mois afficherait sinon la mauvaise période.
  const year = currentYear()
  const month = currentMonth()
  const { from, to } = monthBounds(year, month)

  const showMembers = canManageMembers(roles)
  const showFinance = canViewFinance(roles)

  const [membersCount, servicesThisMonth, bilan, recentServices] = useQueries({
    queries: [
      {
        queryKey: ['members', 'count'],
        // `GET /members` est réservé à pastor/secretary : ne pas interroger
        // pour un trésorier, qui recevrait un 403.
        queryFn: () => apiGetPaginated<unknown>('members', { limit: 1 }),
        enabled: showMembers,
      },
      {
        queryKey: ['services', 'month', from, to],
        // Filtré sur le mois : l'app native affiche ici le total global.
        queryFn: () => apiGetPaginated<Service>('services', { limit: 1, from, to }),
      },
      { ...bilanQuery(year, month), enabled: showFinance },
      recentServicesQuery(3),
    ],
  })

  const roleLabel = ROLE_LABELS[roles[0]] ?? 'Bienvenue'

  return (
    <Screen withTabBar>
      <ScreenHeader title="Bonjour" subtitle={roleLabel} />

      <div className="flex flex-col gap-5 px-5">
        <div className="grid grid-cols-2 gap-3">
          {showMembers && (
            <StatCard
              icon={<Users size={18} color="#C9A84C" aria-hidden="true" />}
              label="Fidèles"
              value={membersCount.data?.meta.total}
              loading={membersCount.isPending}
            />
          )}
          <StatCard
            icon={<CalendarDays size={18} color="#C9A84C" aria-hidden="true" />}
            label="Cultes ce mois"
            value={servicesThisMonth.data?.meta.total}
            loading={servicesThisMonth.isPending}
          />
        </div>

        {showFinance && <BilanCard bilan={bilan.data} loading={bilan.isPending} />}

        <section>
          <h2 className="mb-2.5 text-[15px] font-semibold text-chalk">Derniers cultes</h2>

          {recentServices.isPending && <Skeleton className="h-24 rounded-[14px] bg-navy-900" />}

          {recentServices.data?.data.length === 0 && (
            <EmptyState message="Aucun culte enregistré." />
          )}

          <ul className="flex flex-col gap-2">
            {recentServices.data?.data.map((service) => (
              <li
                key={service.id}
                className="rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3"
              >
                <p className="text-[15px] font-semibold text-chalk">
                  {SERVICE_TYPE_LABELS[service.type] ?? service.type}
                </p>
                <p className="text-[13px] text-steel">
                  {formatDateFR(service.date, { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Screen>
  )
}

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value?: number
  loading: boolean
}

function StatCard({ icon, label, value, loading }: StatCardProps) {
  return (
    <div className="rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3.5">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[12px] text-steel">{label}</span>
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-16 bg-navy-800" />
      ) : (
        <p className="mt-1 text-[26px] font-bold text-chalk">{value ?? '—'}</p>
      )}
    </div>
  )
}

function BilanCard({ bilan, loading }: { bilan?: Bilan; loading: boolean }) {
  const year = currentYear()
  const month = currentMonth()
  const periodLabel = formatDateFR(`${year}-${String(month).padStart(2, '0')}-01`, {
    month: 'long',
    year: 'numeric',
  })

  return (
    <section className="rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-4">
      <h2 className="text-[15px] font-semibold text-chalk">
        Bilan — <span className="capitalize">{periodLabel}</span>
      </h2>

      {loading ? (
        <Skeleton className="mt-3 h-20 bg-navy-800" />
      ) : (
        <dl className="mt-3 flex flex-col gap-2.5">
          <BilanRow label="Recettes" amount={bilan?.totalIncome ?? 0} tone="success" />
          <BilanRow label="Dépenses" amount={bilan?.totalExpense ?? 0} tone="danger" />
          <div className="border-t border-navy-800 pt-2.5">
            <BilanRow
              label="Solde net"
              amount={bilan?.netBalance ?? 0}
              tone={(bilan?.netBalance ?? 0) >= 0 ? 'gold' : 'danger'}
              emphasis
            />
          </div>
        </dl>
      )}
    </section>
  )
}

interface BilanRowProps {
  label: string
  amount: number
  tone: 'success' | 'danger' | 'gold'
  emphasis?: boolean
}

const TONE_CLASS: Record<BilanRowProps['tone'], string> = {
  success: 'text-success',
  danger: 'text-danger',
  gold: 'text-gold',
}

function BilanRow({ label, amount, tone, emphasis }: BilanRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[14px] text-steel">{label}</dt>
      <dd
        className={cn(
          'font-semibold tabular-nums',
          TONE_CLASS[tone],
          emphasis ? 'text-[18px]' : 'text-[15px]',
        )}
      >
        {formatFCFA(amount)}
      </dd>
    </div>
  )
}
